import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Community {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  tags: string[];
  rules?: string[];
  profilePicture?: string;
  bannerImage?: string;
  membersCount: number;
  postsCount: number;
  owner: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  moderators?: Array<{
    id: string;
    username: string;
    profilePicture?: string;
    role?: string;
  }>;
  isMember: boolean;
  userRole: string | null;
  createdAt: string;
  // ⭐ NEW: Additional UI flags from backend
  canEdit?: boolean;
  canDelete?: boolean;
  canManageMembers?: boolean;
}

export interface UserRoleResponse {
  success: boolean;
  data: {
    isOwner: boolean;
    isMember: boolean;
    role: string | null;
    canEdit: boolean;
    communityId: string;
    communityName: string;
  };
}

export interface CommunityResponse {
  success: boolean;
  communities: Community[];
  total?: number;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface SingleCommunityResponse {
  error: string;
  success: boolean;
  community: Community & {
    rules?: string[];
    moderators: Array<{
      id: string;
      username: string;
      profilePicture?: string;
      role?: string;
    }>;
    // ⭐ NEW: Additional UI flags
    canEdit: boolean;
    canDelete: boolean;
    canManageMembers: boolean;
  };
}

export interface EditCommunityData {
  name?: string;
  description?: string;
  category?: string;
  tags?: string[] | string;
  rules?: string[] | string;
  bannerImage?: File;
  profilePicture?: File;
}

export interface Post {
  updatedAt: string | undefined;
  isAuthor: boolean;
  id: string;
  title: string;
  content: string;
  type: 'text' | 'image' | 'video';
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  images?: string[];
  author: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  community?: {
    id: string;
    name: string;
    slug: string;
    profilePicture?: string;
  };
  tags: string[];
  upvotes: number;
  downvotes: number;
  score: number;
  commentsCount: number;
  viewCount: number;
  isPublic: boolean;
  createdAt: string;
  userVote: 'up' | 'down' | null;
}

export interface PostsResponse {
  error: string;
  success: boolean;
  posts: Post[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface CreatePostResponse {
  error: string;
  success: boolean;
  message: string;
  post: Post;
}

export interface JoinCommunityResponse {
  error: string;
  success: boolean;
  message: string;
  community?: {
    id: string;
    name: string;
    slug: string;
    membersCount: number;
  };
}

export interface EditCommunityResponse {
  error: string;
  success: boolean;
  message: string;
  community: Community;
}

export interface TransferOwnershipData {
  newOwnerId: string;
}

export interface ReportCommunityData {
  reportType: string;
  customReason?: string;
  description?: string;
  communityName?: string;
  communityOwnerId?: string;
  communityOwnerUsername?: string;
  communityDescription?: string;
}

export interface ReportResponse {
  success: boolean;
  message: string;
  report?: {
    id: string;
    targetType: string;
    status: string;
    timestamp: string;
  };
  error?: string;
}

export const communityApi = {
  // Get all communities
  getCommunities: async (params?: {
    category?: string;
    search?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
  }): Promise<CommunityResponse> => {
    const response = await api.get('/api/community', { params });
    return response.data;
  },

  // Get user's communities
  getUserCommunities: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<CommunityResponse> => {
    const response = await api.get('/api/community/user/communities', { params });
    return response.data;
  },

  // Get single community
  getCommunity: async (slug: string): Promise<SingleCommunityResponse> => {
    const response = await api.get(`/api/community/${slug}`);
    return response.data;
  },

  // Check user role in community
  checkUserRole: async (communityId: string): Promise<UserRoleResponse> => {
    const response = await api.get(`/api/community/${communityId}/check-role`);
    return response.data;
  },

  // Create community
  createCommunity: async (data: FormData): Promise<{
    error: string; 
    success: boolean; 
    community: Community; 
    message: string 
  }> => {
    const response = await api.post('/api/community', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    return response.data;
  },

  // Edit community (owner only)
  editCommunity: async (
    communityId: string,
    data: FormData
  ): Promise<EditCommunityResponse> => {
    const response = await api.put(`/api/community/${communityId}/edit`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    return response.data;
  },

  // Transfer ownership (owner only)
  transferOwnership: async (
    communityId: string,
    data: TransferOwnershipData
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/api/community/${communityId}/transfer-ownership`, data);
    return response.data;
  },

  // Join community
  joinCommunity: async (communityId: string): Promise<JoinCommunityResponse> => {
    const response = await api.post(`/api/community/${communityId}/join`);
    return response.data;
  },

  // Leave community
  leaveCommunity: async (communityId: string): Promise<{
    error: string; 
    success: boolean; 
    message: string 
  }> => {
    const response = await api.post(`/api/community/${communityId}/leave`);
    return response.data;
  },

  // Get community posts (posts tagged with this community)
  getCommunityPosts: async (
    communityId: string,
    params?: {
      sortBy?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<PostsResponse> => {
    const response = await api.get(`/api/community/${communityId}/posts`, { params });
    return response.data;
  },

  // Get community members
  getCommunityMembers: async (
    communityId: string,
    params?: {
      role?: string;
      search?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<{
    success: boolean;
    members: Array<{
      id: string;
      user: {
        id: string;
        username: string;
        profilePicture?: string;
        createdAt: string;
      };
      role: string;
      joinedAt: string;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }> => {
    const response = await api.get(`/api/community/${communityId}/members`, { params });
    return response.data;
  },

  reportCommunity: async (
    communityId: string,
    data: ReportCommunityData
  ): Promise<ReportResponse> => {
    const response = await api.post('/api/report-community/community', {
      communityId,
      ...data
    });
    return response.data;
  },

  // Check if user has reported community recently
  checkCommunityReport: async (communityId: string): Promise<{
    success: boolean;
    canReport: boolean;
    lastReport?: Date;
    coolDownRemaining?: number;
    reason?: string;
    error?: string;
  }> => {
    const response = await api.get(`/api/report-community/community/check/${communityId}`);
    return response.data;
  },

  // Get user's community reports
  getUserCommunityReports: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    reports: Array<{
      id: string;
      communityId: string;
      communityName: string;
      reportType: string;
      status: string;
      timestamp: string;
      reviewedAt?: string;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }> => {
    const response = await api.get('/api/report-community/community/user-reports', { params });
    return response.data;
  },
};

export default api;